import {
  CreateSalesPriceConditionForm,
  SalesPriceConditionSchema,
} from '@retail/calculus/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew, MarkupBinding } from '@retail/calculus/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { CreateItemLevelSalesPriceConditionForm } from '../CreateSalesPriceCondition/CreateItemLevelSalesPriceConditionForm';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { useConditionActions } from '@retail/calculus/context';
import { getISO_8601Date } from '@retail/utils';
import { EditConditionDialog } from '../../ConditionActionsBase';

interface Props {
  open: boolean;
  includeMarkup: boolean;
  salesPriceCondition: ConditionsNew;
  availablePriceSuggestionTypes: PriceSuggestionTypeWithCount[];
  currentPriceSuggestionPriority: PriceSuggestionType[];
  levelName: string;
  isLoadingFetchPriceSuggestions: boolean;
  onClose: () => void;
}

export function EditSalesPriceConditionDialog({
  open,
  includeMarkup,
  salesPriceCondition,
  availablePriceSuggestionTypes,
  currentPriceSuggestionPriority,
  levelName,
  isLoadingFetchPriceSuggestions,
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const { createMultipleConditions, removeCondition } = useConditionActions();

  const markupCondition: MarkupBinding | undefined = useMemo(() => {
    const markupCondition = salesPriceCondition.markupCondition;
    return markupCondition
      ? {
          ...markupCondition,
          bindingInfo: markupCondition.inheritedBinding ?? {
            bindingType: salesPriceCondition.bindingType,
            bindingTargetId: salesPriceCondition.bindingTargetId,
            bindingTargetName: salesPriceCondition.bindingTargetName,
          },
        }
      : undefined;
  }, [
    salesPriceCondition.bindingTargetId,
    salesPriceCondition.bindingTargetName,
    salesPriceCondition.bindingType,
    salesPriceCondition.markupCondition,
  ]);

  const { control, reset, handleSubmit } = useFormContext<SalesPriceConditionSchema>();
  const priceSourceController = useController({ name: 'priceSourcePriority', control });
  const markupCoverageDegreeController = useController({ name: 'markupCoverageDegree', control });

  const updatedPriceSuggestionPriority: PriceSuggestionType[] = useMemo(
    () => priceSourceController.field.value,
    [priceSourceController.field.value]
  );
  const submitSalesPriceConditions = useCallback(
    (data: SalesPriceConditionSchema) => {
      const priceSuggestion = {
        content: {
          priceSuggestion: {
            priorities: data.priceSourcePriority.includes('MARKUP')
              ? data.priceSourcePriority
              : [...(data.priceSourcePriority ?? []), 'MARKUP'],
          },
        },
        binding: salesPriceCondition.getBindingLevel(),
        validFrom: getISO_8601Date(data.fromDate),
      };
      const markup =
        data.markupCoverageDegree != null
          ? {
              content: {
                markupCoverageDegree: { coverageDegree: data.markupCoverageDegree },
              },
              binding: salesPriceCondition.getBindingLevel(),
              validFrom: getISO_8601Date(data.fromDate),
            }
          : undefined;
      createMultipleConditions(markup ? [priceSuggestion, markup] : [priceSuggestion]);

      if (
        data.markupCoverageDegree == null &&
        salesPriceCondition.markupCondition &&
        !salesPriceCondition.markupCondition.isInherited
      ) {
        removeCondition([salesPriceCondition.markupCondition.conditionId], 'deactivate');
      }

      reset();
      onClose();
    },
    [salesPriceCondition, createMultipleConditions, reset, onClose, removeCondition]
  );

  const changeInPriceSuggestion = useMemo(() => {
    const updatedPrio = updatedPriceSuggestionPriority.filter((prio) => prio !== 'MARKUP');
    const currentPrio = currentPriceSuggestionPriority.filter((prio) => prio !== 'MARKUP');

    return (
      updatedPrio?.length !== currentPrio?.length ||
      !updatedPrio.every((val, index) => val === currentPrio[index])
    );
  }, [currentPriceSuggestionPriority, updatedPriceSuggestionPriority]);

  const changeInMarkupCoverageDegree = useMemo(
    () =>
      salesPriceCondition.markupCondition?.isInherited
        ? !!markupCoverageDegreeController.field.value
        : Number(markupCoverageDegreeController.field.value) !==
          salesPriceCondition.markupCondition?.coverageDegree,
    [
      markupCoverageDegreeController.field.value,
      salesPriceCondition.markupCondition?.coverageDegree,
      salesPriceCondition.markupCondition?.isInherited,
    ]
  );
  return (
    <EditConditionDialog
      open={open}
      title={t('calculus.salesPriceCondition.editSalesPriceCondition', { levelName })}
      dialogContent={
        !!salesPriceCondition.bindingTargetItem?.mgItemNumber &&
        salesPriceCondition.supplier?.supplierNumber ? (
          <CreateItemLevelSalesPriceConditionForm
            mgSupplierId={salesPriceCondition.supplier?.supplierNumber}
            mgItemNumber={salesPriceCondition.bindingTargetItem?.mgItemNumber}
            includeMarkup={includeMarkup}
            markupCondition={markupCondition}
            isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
          />
        ) : (
          <CreateSalesPriceConditionForm
            priceSuggestionTypes={availablePriceSuggestionTypes}
            markupCondition={markupCondition}
            includeMarkup={includeMarkup}
            isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
          />
        )
      }
      isDisabled={!changeInPriceSuggestion && !changeInMarkupCoverageDegree}
      onSubmit={handleSubmit(submitSalesPriceConditions)}
      onClose={onClose}
    />
  );
}
